import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Uhodni_slovo (Guess the Word) - Word guessing layout
 *
 * Layout: Portrait page, 1×10 grid.
 * Each row: image on left, letter circles on right.
 * ~50% of letters are revealed, student guesses the rest.
 *
 * Migrated from C# EduFrame.Img10.Generators.Uhodni_slovo
 */
export class UhodniSlovo implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const itemsPerPage = 10;
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(1, itemsPerPage, margin, PdfSize.A4);
    const {random} = opts.data;

    const totalCount = Math.min(opts.list.length, this.countMax());

    // Find max label length for sizing across ALL items to keep it consistent
    const maxLen = opts.list
      .slice(0, totalCount)
      .reduce((max, item) => Math.max(max, item.text.length + 1), 0);

    const sz = Math.min((size.width - size.height - 10) / maxLen, size.height * 0.9);
    const origin1x = size.height + (PdfSize.A4.width - maxLen * sz - size.height) / 2;
    const origin1y = origin.y + (size.height - sz) / 2;

    let labelFont: PdfFont | undefined;

    for (let pIdx = 0; pIdx < Math.ceil(totalCount / itemsPerPage); pIdx++) {
      const page = pdf.addPage();
      const start = pIdx * itemsPerPage;
      const end = Math.min(start + itemsPerPage, totalCount);

      if (!labelFont) {
        const allLabelTexts = opts.list
          .slice(0, totalCount)
          .map((item) => [item.label.getText() ?? '']);
        const labelSize = new PdfSize(size.height, size.height);
        labelFont = page.adjustFontMulti(allLabelTexts, GUtil.baseFont, labelSize);
      }

      for (let i = start; i < end; i++) {
        const localIdx = i - start;
        const rect = new PdfRect(
          origin.x,
          origin.y + localIdx * size.height,
          size.width,
          size.height
        );
        const imgRect = new PdfRect(rect.x, rect.y, rect.height, rect.height);

        page.drawLabel(opts.list[i].label, { rect: imgRect, font: labelFont });
        const label = opts.list[i].text;

        // Create mask: half revealed, half hidden, shuffled
        const mask: boolean[] = [];
        for (let j = 0; j < label.length; j++) {
          mask.push(j >= Math.floor(label.length / 2));
        }
        // Shuffle mask
        for (let j = mask.length - 1; j > 0; j--) {
          const k = random.next(0, j + 1);
          [mask[j], mask[k]] = [mask[k], mask[j]];
        }

        for (let j = 0; j < label.length; j++) {
          if (/\s/.test(label[j])) continue;

          const r = new PdfRect(origin1x + sz * j, origin1y + localIdx * size.height, sz, sz);
          page.drawEllipse(GUtil.basePen, null, r);

          if (mask[j]) {
            page.drawString(
              label[j].toUpperCase(),
              GUtil.baseFont,
              PdfBrush.Black,
              null,
              r,
              ETextAlign.Center
            );
          }
        }
      }
    }
  }
}
